import React, { useState, useEffect, useMemo } from 'react';
import {
  Button, Calendar, Card, Columns03, EmptyState, FilterBar, Metric, Pagination, SearchLg, Table, TextInput,
} from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import TableSettings from '../../shared/components/TableSettings';
import useReportingPage from '../hooks/useReportingPage';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';
import '../styles/reportingStyles.css';

const axios = require('axios').default;
const qs = require('qs');

const noop = () => {};
const PER_PAGE = 25;

const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
].map((label, i) => ({ value: String(i + 1), label }));

const RANGE_FILTERS = {
  total_given: { label: 'Total given', prefix: '$' },
  largest_gift: { label: 'Largest gift', prefix: '$' },
  gift_count: { label: 'Number of gifts' },
};

const COLUMN_IDS = ['donor_name', 'email', 'total_given', 'gift_count', 'largest_gift', 'first_gift_at', 'last_gift_at', 'lifetime_given'];

const columnsStorageKey = (teamId) => `reportingDonationsAllDonorsColumns_${teamId}`;

const loadColumnLayout = (teamId) => {
  try {
    const saved = JSON.parse(localStorage.getItem(columnsStorageKey(teamId)));
    const visible = saved.visible.filter((id) => COLUMN_IDS.includes(id));
    if (!visible.includes('donor_name')) throw new Error('invalid layout');
    return { visible, hidden: COLUMN_IDS.filter((id) => !visible.includes(id)) };
  } catch (e) {
    return { visible: COLUMN_IDS, hidden: [] };
  }
};

const formatDay = (value) => (value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const DATE_FILTERS = {
  last_gift_after: 'Last gift on or after',
  last_gift_before: 'Last gift on or before',
};

const pad = (n) => String(n).padStart(2, '0');
const toIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIsoDate = (iso) => new Date(`${iso}T00:00:00`);

const formatBound = (filter, value) => (RANGE_FILTERS[filter.key]?.prefix ? currencyFormatter.format(value) : value);

const formatFilterValue = (filter, values) => {
  if (!filter.render) {
    if (!values?.length) return 'Any';
    if (values.length > 1) return `${values.length} selected`;
    return filter.options.find((option) => option.value === values[0])?.label;
  }
  if (DATE_FILTERS[filter.key]) return values?.length ? formatDay(fromIsoDate(values[0])) : 'Any';
  const [min, max] = values || [];
  if (min && max) return `${formatBound(filter, min)} – ${formatBound(filter, max)}`;
  if (min) return `At least ${formatBound(filter, min)}`;
  if (max) return `At most ${formatBound(filter, max)}`;
  return 'Any';
};

function BoundsEditor({ values, onChange, close, prefix }) {
  const [min, setMin] = useState(values[0] || '');
  const [max, setMax] = useState(values[1] || '');

  const apply = () => {
    onChange(min || max ? [min, max] : []);
    close();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12, minWidth: 240 }}>
      <TextInput size="sm" type="text" inputMode="decimal" label="Minimum" leadingText={prefix} value={min} onChange={(e) => setMin(e.target.value.replace(/[^\d.]/g, ''))} />
      <TextInput size="sm" type="text" inputMode="decimal" label="Maximum" leadingText={prefix} value={max} onChange={(e) => setMax(e.target.value.replace(/[^\d.]/g, ''))} />
      <Button size="sm" variant="primary" onClick={apply}>Apply</Button>
    </div>
  );
}

function DateEditor({ values, onChange, close }) {
  return (
    <div className="p-2">
      <Calendar
        value={values.length ? fromIsoDate(values[0]) : null}
        onChange={(date) => {
          onChange(date ? [toIsoDate(date)] : []);
          close();
        }}
      />
    </div>
  );
}

const dateFilter = (key) => ({
  key,
  label: DATE_FILTERS[key],
  render: (editor) => <DateEditor {...editor} />,
});

const rangeFilter = (key) => ({
  key,
  label: RANGE_FILTERS[key].label,
  render: (editor) => (
    <BoundsEditor {...editor} prefix={RANGE_FILTERS[key].prefix} />
  ),
});

const boundsParams = (params, values, minKey, maxKey) => {
  const [min, max] = values || [];
  if (min) params[minKey] = min;
  if (max) params[maxKey] = max;
};

function ReportingDonationsAllDonorsPage({ team, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    activeFilters, setActiveFilters,
  } = useReportingPage('all_time');

  const [filterOptions, setFilterOptions] = useState({ giving_campaigns: [], gift_years: [] });
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageFor, setPageFor] = useState({ key: null, page: 1 });
  const [sort, setSort] = useState({ key: 'total_given', direction: 'desc' });
  const [columnLayout, setColumnLayout] = useState(() => loadColumnLayout(team?.id));
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [shownFilters, setShownFilters] = useState(['total_given']);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!team?.id) return;
    axios.get(`/teams/${team.id}/reporting/donations/all-donors/filter_options`)
      .then((response) => setFilterOptions(response.data))
      .catch((requestError) => console.error('Failed to load All Donors filter options', requestError));
  }, [team]);

  const filterDefinitions = useMemo(() => {
    const definitions = [];
    if (venueOwnerships?.length > 1) {
      definitions.push({
        key: 'venue_ownership_id',
        label: 'Venue',
        options: venueOwnerships.map((vo) => ({ value: String(vo.id), label: vo.name || `Venue ${vo.id}` })),
      });
    }
    definitions.push(
      rangeFilter('total_given'),
      rangeFilter('largest_gift'),
      rangeFilter('gift_count'),
      dateFilter('last_gift_after'),
      dateFilter('last_gift_before'),
      { key: 'months', label: 'Gift month', options: MONTH_OPTIONS },
      {
        key: 'years',
        label: 'Gift year',
        options: (filterOptions.gift_years || []).map((year) => ({ value: String(year), label: String(year) })),
      },
      {
        key: 'giving_campaign_id',
        label: 'Campaign',
        options: (filterOptions.giving_campaigns || []).map((campaign) => ({ value: String(campaign.id), label: campaign.name })),
      },
    );
    return definitions;
  }, [venueOwnerships, filterOptions]);

  const apiParams = useMemo(() => {
    const params = {
      start_date: dateRange?.start || undefined,
      end_date: dateRange?.end || undefined,
      search: search || undefined,
      sort_by: [sort.key],
      sort_direction: [sort.direction],
    };
    ['venue_ownership_id', 'months', 'years', 'giving_campaign_id'].forEach((key) => {
      if (activeFilters[key]?.length) params[key] = activeFilters[key];
    });
    Object.keys(RANGE_FILTERS).forEach((key) => boundsParams(params, activeFilters[key], `${key}_min`, `${key}_max`));
    Object.keys(DATE_FILTERS).forEach((key) => {
      if (activeFilters[key]?.length) [params[key]] = activeFilters[key];
    });
    return params;
  }, [dateRange, search, sort, activeFilters]);

  const paramsKey = JSON.stringify(apiParams);
  const page = pageFor.key === paramsKey ? pageFor.page : 1;
  const setPage = (next) => setPageFor({ key: paramsKey, page: next });

  useEffect(() => {
    if (!team?.id) return undefined;
    let ignore = false;
    setLoading(true);
    setError(null);
    axios.get(`/teams/${team.id}/reporting/donations/all-donors/data`, {
      params: { ...apiParams, page, per_page: PER_PAGE },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
    })
      .then((response) => {
        if (ignore) return;
        setRows(response.data.donors || []);
        setKpis(response.data.kpis || null);
        setPagination(response.data.pagination || null);
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error('Failed to load All Donors report', requestError);
        setError('Failed to load donors.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => { ignore = true; };
  }, [team, apiParams, page]);

  const exportUrl = `/teams/${team?.id}/reporting/donations/all-donors/export?${qs.stringify(apiParams, { arrayFormat: 'brackets' })}`;

  const allColumns = [
    {
      key: 'donor_name',
      header: 'Donor',
      sortable: true,
      primary: true,
      render: (row) => (team?.package?.crm && row.id
        ? <a href={`/teams/${team.id}/crm/fans/${row.id}`}>{row.donor_name}</a>
        : row.donor_name),
    },
    { key: 'email', header: 'Email', sortable: true, render: (row) => row.email || '—' },
    { key: 'total_given', header: 'Total Given', sortable: true, align: 'right', render: (row) => currencyFormatter.format(row.total_given) },
    { key: 'gift_count', header: 'Gifts', sortable: true, align: 'right', render: (row) => row.gift_count.toLocaleString() },
    { key: 'largest_gift', header: 'Largest Gift', sortable: true, align: 'right', render: (row) => currencyFormatter.format(row.largest_gift) },
    { key: 'first_gift_at', header: 'First Gift', sortable: true, render: (row) => formatDay(row.first_gift_at) },
    { key: 'last_gift_at', header: 'Last Gift', sortable: true, render: (row) => formatDay(row.last_gift_at) },
    { key: 'lifetime_given', header: 'Lifetime Given', sortable: true, align: 'right', render: (row) => currencyFormatter.format(row.lifetime_given) },
  ];
  const columnsById = Object.fromEntries(allColumns.map((col) => [col.key, col]));
  const columns = columnLayout.visible.map((id) => ({
    ...columnsById[id],
    render: (row) => <span style={{ whiteSpace: 'nowrap' }}>{columnsById[id].render(row)}</span>,
  }));
  const toField = (id) => ({ id, label: columnsById[id].header, alwaysVisible: id === 'donor_name' });

  const handleColumnsChange = (visible, hidden) => {
    const layout = { visible: visible.map((f) => f.id), hidden: hidden.map((f) => f.id) };
    setColumnLayout(layout);
    try {
      localStorage.setItem(columnsStorageKey(team?.id), JSON.stringify(layout));
    } catch (e) {
      console.error('Failed to save All Donors column layout', e);
    }
  };

  const kpiCards = [
    { label: 'Donors', value: kpis?.donor_count.toLocaleString() },
    { label: 'Total Given', value: kpis && compactCurrency(kpis.total_given) },
    { label: 'Gifts', value: kpis?.gift_count.toLocaleString() },
    { label: 'Average Gift', value: kpis && currencyFormatter.format(kpis.avg_gift) },
  ];

  return (
    <ReportingPageLayout
      team={team}
      activeKey="donations_all_donors"
      title="All Donors"
      subtitle="Every donor from ticket checkouts and giving campaigns. The date range, gift month, gift year and campaign filters choose which gifts count toward Total Given; First Gift, Last Gift and Lifetime Given cover all of a donor's gifts."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={(
        <div className="ml-auto d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Button size="sm" variant="secondary" icon={Columns03} onClick={() => setTableSettingsOpen(!tableSettingsOpen)}>
              Table Settings
            </Button>
            <TableSettings
              isOpen={tableSettingsOpen}
              onClose={() => setTableSettingsOpen(false)}
              visibleFields={columnLayout.visible.map(toField)}
              hiddenFields={columnLayout.hidden.map(toField)}
              onFieldsChange={handleColumnsChange}
            />
          </div>
          <Button size="sm" as="a" href={exportUrl} variant="secondary">Export CSV</Button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: 16, marginBottom: 16 }}>
        {kpiCards.map((card) => <Metric key={card.label} label={card.label} value={card.value ?? '—'} />)}
      </div>

      <Card padding="none">
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--sl-border-secondary, #e4e7ec)' }}>
          <FilterBar
            variant="plain"
            filters={filterDefinitions}
            values={activeFilters}
            onChange={setActiveFilters}
            shown={shownFilters}
            onShownChange={setShownFilters}
            formatValue={formatFilterValue}
            resultCount={pagination ? `${pagination.total_entries.toLocaleString()} donors` : undefined}
            search={(
              <TextInput
                size="sm"
                leadingIcon={SearchLg}
                placeholder="Search donors"
                aria-label="Search donors"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            )}
          />
        </div>
        <Table
          stickyFirstColumn
          columns={columns}
          data={rows}
          getRowKey={(row) => row.id}
          loading={loading}
          error={error}
          sort={sort}
          onSortChange={(key, direction) => setSort({ key, direction })}
          empty={<EmptyState size="sm" icon={SearchLg} title="No donors match these filters." />}
        />
        {pagination?.total_pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--sl-border-secondary, #e4e7ec)' }}>
            <Pagination currentPage={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </ReportingPageLayout>
  );
}

export default ReportingDonationsAllDonorsPage;
